import { useCallback, useEffect, useState } from 'react';

/**
 * V1 Voice GM (2026-06-02) — narrator voice persistence.
 *
 * Stores the selected Kokoro voice id (e.g. "bm_lewis", "bf_emma",
 * "af_heart") in localStorage so the choice survives reloads + cross-
 * paragraph navigation. Default is `bm_lewis` (British Male) per the
 * V13 §21 user pick.
 *
 * 2026-06-08 Fix #F — read path now validates the stored value against
 * the Kokoro voice-id pattern (one letter + underscore + 2–20 lowercase
 * alphanumerics) before returning it. Pre-fix any non-empty string was
 * accepted, including stale ids from removed voices, multi-kilobyte
 * payloads from copy-paste accidents, or "garbage" from a hand-edit.
 * Invalid stored values now fall back to `NARRATOR_VOICE_DEFAULT`
 * silently — same recovery posture as the corrupt-JSON path on other
 * persisted keys.
 *
 * Future (post-V1): per-campaign voice override, per-NPC voice maps,
 * GM-voice-swap mid-session (V13 §21 Q7).
 */

const STORAGE_KEY = 'briefing-room:narrator-voice';
export const NARRATOR_VOICE_DEFAULT = 'bm_lewis';

/**
 * Kokoro voice ids follow a `<gender-language>_<name>` shape: a single
 * lowercase letter (`a`/`b`/`m`/`f`/etc. depending on gender + accent),
 * an underscore, then 2–20 lowercase alphanumeric characters. The regex
 * is intentionally narrow — wider patterns admit junk payloads that
 * flow into TTS request URLs.
 */
const VOICE_ID_PATTERN = /^[a-z]{1,3}_[a-z0-9]{2,20}$/;

export function isValidNarratorVoiceId(value: unknown): value is string {
  return typeof value === 'string' && VOICE_ID_PATTERN.test(value);
}

function readStored(): string {
  if (typeof window === 'undefined') {
    return NARRATOR_VOICE_DEFAULT;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isValidNarratorVoiceId(stored)) {
      return stored;
    }
  } catch {
    // localStorage may throw on privacy-mode or quota — degrade quietly.
  }
  return NARRATOR_VOICE_DEFAULT;
}

function writeStored(value: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  // 2026-06-08 Fix #F — refuse to persist an invalid voice id so a
  // future caller (a misbehaving extension, a stale setter, etc.)
  // can't poison the key.
  if (!isValidNarratorVoiceId(value)) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Same degradation pattern.
  }
}

export function useNarratorVoice(): [string, (next: string) => void] {
  const [voice, setVoiceState] = useState<string>(readStored);

  useEffect(() => {
    // Sync with same-tab updates from other components (e.g. a future
    // header chip that also writes the key). StorageEvent fires for
    // cross-tab; we use a custom event for same-tab sync.
    const handler = (e: Event) => {
      if (e instanceof CustomEvent && typeof e.detail === 'string') {
        if (isValidNarratorVoiceId(e.detail)) {
          setVoiceState(e.detail);
        }
        return;
      }
      setVoiceState(readStored());
    };
    window.addEventListener('briefing-room:narrator-voice-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('briefing-room:narrator-voice-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setVoice = useCallback((next: string) => {
    if (!isValidNarratorVoiceId(next)) {
      return;
    }
    setVoiceState(next);
    writeStored(next);
    window.dispatchEvent(
      new CustomEvent('briefing-room:narrator-voice-changed', { detail: next }),
    );
  }, []);

  return [voice, setVoice];
}
